import React from "react";
import { BarChart3 } from "lucide-react";
import { Card } from "../../common/Primitives";

/**
 * 7-day reviewer throughput bars — Approved / More Info / Rejected stacked
 * per reviewer per day. Purely presentational; data comes from
 * GET /admin/kyc/workload-chart?days=7.
 */
export default function WorkloadChart({ chart }) {
  const reviewers = chart?.reviewers || [];
  if (reviewers.length === 0) return null;
  // Max stacked value across the whole chart so bars are comparable.
  const maxDay = Math.max(1, ...reviewers.flatMap((r) => (r.days || []).map((d) => d.total)));
  const dayLabels = (chart.days || []).map((d) =>
    new Date(d).toLocaleDateString("en-IN", { weekday: "short", day: "numeric" }),
  );
  return (
    <Card testId="kyc-workload-chart" className="mb-4">
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="overline flex items-center gap-1.5"><BarChart3 size={12} /> 7-Day Reviewer Throughput</div>
          <div className="text-xs text-ink-soft mt-0.5">
            Bars = decisions per day (Approved / More Info / Rejected). Ordered by total.
          </div>
        </div>
        <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest">
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-emerald-500" /> Approved</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-amber-500" /> More Info</span>
          <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-sm bg-rose-500" /> Rejected</span>
        </div>
      </div>
      <div className="space-y-4">
        {reviewers.map((r) => (
          <div key={r.user_id} data-testid={`kyc-chart-${r.user_id}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="font-heading text-ink text-sm truncate">{r.name}</div>
              <div className="text-[11px] font-mono text-ink-muted">
                {r.total} decisions · {r.role}
              </div>
            </div>
            <div className="grid grid-cols-7 gap-1.5">
              {r.days.map((d, i) => {
                const barHeightPct = d.total ? Math.max(6, (d.total / maxDay) * 100) : 0;
                return (
                  <div key={d.date} className="flex flex-col items-center" title={`${d.date}: ${d.approved} approved, ${d.more_info} more-info, ${d.rejected} rejected`}>
                    <div className="w-full h-16 relative bg-navy/[0.04] rounded overflow-hidden flex flex-col justify-end">
                      {d.total > 0 && (
                        <>
                          {d.rejected > 0 && (
                            <div className="w-full bg-rose-500" style={{ height: `${(d.rejected / d.total) * barHeightPct}%` }} />
                          )}
                          {d.more_info > 0 && (
                            <div className="w-full bg-amber-500" style={{ height: `${(d.more_info / d.total) * barHeightPct}%` }} />
                          )}
                          {d.approved > 0 && (
                            <div className="w-full bg-emerald-500" style={{ height: `${(d.approved / d.total) * barHeightPct}%` }} />
                          )}
                        </>
                      )}
                    </div>
                    <div className="text-[9px] font-mono text-ink-muted mt-1 truncate max-w-full">{dayLabels[i]}</div>
                  </div>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
